'use client';

import { Mail, ChevronUp, ChevronDown } from 'lucide-react';
import Link from 'next/link';
import CTAButton from './CTAbutton';
import BrandLogo from '@/components/common/BrandLogo';
import { NavigationItem } from '@/components/common/StickyHeaderNav';
import { useMemo, useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { emailUs, scheduleCall } from '@/lib/cta';

const Footer = () => {
  const [quickLinksOpen, setQuickLinksOpen] = useState(false);

  const navigationItems: NavigationItem[] = useMemo(
    () => [
      { label: 'Process', anchor: '#process', description: '28-day development timeline' },
      { label: 'Launches', anchor: '#case-studies', description: 'Recent launches showcase' },
      { label: 'FAQs', anchor: '#faq', description: 'Frequently Asked Questions' },
      { label: 'About', anchor: '#about', description: 'About our company' },
    ],
    []
  );

  const handleAnchorClick = (anchor: string) => {
    const element = document.getElementById(anchor.substring(1));
    if (element) {
      window.scrollTo({ top: element.offsetTop - 60, behavior: 'smooth' });
    }
  };

  return (
    <footer className="relative z-10 border-t border-border bg-background pb-16 lg:pb-0">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-8 sm:py-16 lg:px-16">
        <div className="flex flex-col gap-12 lg:flex-row lg:justify-between">
          <div className="max-w-xl flex-1">
            <Link href="/" className="mb-6 inline-block sm:mb-8" aria-label="Nens Informatics home">
              <BrandLogo tone="dark" markSize={40} className="text-lg" />
            </Link>

            <h2 className="mb-4 font-display text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              Have an idea? Let&apos;s build it.
            </h2>
            <p className="mb-8 text-base text-text-secondary">
              Hop on a call with us to see how we can accelerate your growth.
            </p>

            <div className="flex flex-wrap gap-3">
              <CTAButton
                label="Schedule a Call"
                onClick={scheduleCall}
                type="primary"
                icon="PhoneArrowUpRightIcon"
              />
              <CTAButton
                label="Contact Us"
                onClick={() => {
                  window.location.href = emailUs;
                }}
                type="secondary"
                icon={<Mail size={16} />}
              />
            </div>
          </div>

          <div className="hidden lg:block">
            <h4 className="mb-5 font-sans text-xs font-semibold uppercase tracking-[0.15em] text-text-secondary">
              Quick links
            </h4>
            <ul className="space-y-3">
              {navigationItems.map((item) => (
                <li key={item.label}>
                  <button
                    onClick={() => handleAnchorClick(item.anchor)}
                    className="animated-underline text-sm font-medium text-text-secondary hover:text-foreground"
                    aria-label={item.description}
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:hidden">
            <Collapsible open={quickLinksOpen} onOpenChange={setQuickLinksOpen}>
              <CollapsibleTrigger className="flex w-full items-center justify-between border-y border-border py-4">
                <span className="text-xs font-semibold uppercase tracking-[0.15em] text-text-secondary">
                  Quick links
                </span>
                {quickLinksOpen ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </CollapsibleTrigger>
              <CollapsibleContent>
                <ul className="space-y-3 py-4">
                  {navigationItems.map((item) => (
                    <li key={item.label}>
                      <button
                        onClick={() => handleAnchorClick(item.anchor)}
                        className="text-sm font-medium text-text-secondary"
                        aria-label={item.description}
                      >
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>
      </div>

      <div className="border-t border-border px-5 py-5 text-center sm:px-8">
        <p className="text-xs text-text-secondary">
          © {new Date().getFullYear()} Nens Informatics. All rights reserved.
        </p>
      </div>
    </footer>
  );
};

export default Footer;
